import { useState, useRef, useCallback } from 'react';

interface UseTextToSpeechReturn {
  isPlaying: boolean;
  isSynthesizing: boolean;
  playText: (text: string) => Promise<void>;
  stopPlayback: () => void;
  error: string | null;
}

const BETTY_API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://blagmanbros.com/api' 
  : 'http://localhost:8002/api';

export const useTextToSpeech = (): UseTextToSpeechReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentUrlRef = useRef<string | null>(null);

  const playText = useCallback(async (text: string) => {
    try {
      setError(null);
      setIsSynthesizing(true);
      
      // Stop any current playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      // Clean up previous audio URL
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
      
      // Send text to synthesis API
      const response = await fetch(`${BETTY_API_URL}/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text })
      });
      
      if (!response.ok) {
        throw new Error(`TTS synthesis failed: ${response.statusText}`);
      }
      
      // Get audio blob from response
      const audioBlob = await response.blob();
      
      if (audioBlob.size === 0) {
        throw new Error('No audio data received');
      }
      
      // Create audio URL and play
      const audioUrl = URL.createObjectURL(audioBlob);
      currentUrlRef.current = audioUrl;
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      // Set up event handlers
      audio.onloadstart = () => {
        setIsSynthesizing(false);
        setIsPlaying(true);
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        // Clean up
        if (currentUrlRef.current) {
          URL.revokeObjectURL(currentUrlRef.current);
          currentUrlRef.current = null;
        }
        audioRef.current = null;
      };
      
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        setError('Failed to play audio');
        setIsPlaying(false);
        setIsSynthesizing(false);
      };
      
      // Start playback
      await audio.play();
      
    } catch (err) {
      console.error('Error synthesizing speech:', err);
      setError(err instanceof Error ? err.message : 'Failed to synthesize speech');
      setIsSynthesizing(false);
      setIsPlaying(false);
    }
  }, []);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
    
    // Clean up
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }
    audioRef.current = null;
  }, []);

  return {
    isPlaying,
    isSynthesizing,
    playText,
    stopPlayback,
    error
  };
};
